/**
 * NAR-RPA trigger, comment-RPA save, and close handlers for header modals.
 * @module headerModals/hooks/useHeaderModalSecondaryHandlers
 */

import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useInitialDataOfSingleMedicalOrder } from "../../../../../context/initialDataOfSingleMedicalOrder";
import { usePayerNameContextFromSingleOrderLayout } from "../../../../../context/payerNameContextFromSingleOrderLayout";
import {
  AuthOnFile,
  GeneralOrderDetails,
  JCodeDetails,
  SingleCoverage,
} from "../../../../../data-model/medicalPaOrdersModel";
import { ModalNamesForBigQuery } from "../../../../../enums/eventsData";
import { ModalId } from "../../../../../enums/modalId";
import {
  closeModal,
  setOpenedModalId,
} from "../../../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../../../redux/store/store";
import { logTriggerRpaClickedNar } from "../../../utils/headerEventLogger";
import { fireMarkAsCompletedAnalytics } from "../utils/fireMarkAsCompletedAnalytics";
import { navigateToWorklistsAfterMarkComplete } from "../utils/navigateToWorklistsAfterMarkComplete";

/** Parameters for {@link useHeaderModalSecondaryHandlers}. */
interface UseHeaderModalSecondaryHandlersParams {
  /** General order details for order context. */
  generalOrderDetails?: GeneralOrderDetails;
  /** Auth-on-file data for J-code analytics. */
  authOnFile?: AuthOnFile;
  /** Coverage slices used for analytics payloads. */
  coverageDetails?: SingleCoverage[];
  /** Callback that logs per-J-code status events. */
  logJCodeStatusEvents: (jCodes: JCodeDetails[], delayMs: number) => void;
}

/** Return value of {@link useHeaderModalSecondaryHandlers}. */
export interface UseHeaderModalSecondaryHandlersResult {
  /** Opens the NAR RPA trigger modal and logs analytics. */
  handleNarRpaTriggerClick: () => void;
  /** Logs comment-RPA analytics and navigates to worklists. */
  handleCommentRpaModalSave: () => void;
  /** Dispatches the Redux close-modal action. */
  handleCloseModal: () => void;
}

/**
 * Handlers for header modals other than the happy-flow save sequence.
 *
 * @param params - Order context and J-code analytics callback
 * @returns NAR trigger, comment-RPA save, and close handlers
 */
export const useHeaderModalSecondaryHandlers = (
  params: UseHeaderModalSecondaryHandlersParams,
): UseHeaderModalSecondaryHandlersResult => {
  const {
    generalOrderDetails,
    authOnFile,
    coverageDetails,
    logJCodeStatusEvents,
  } = params;

  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const { oldAuthStatus: oldAuthStatusFromContext } =
    useInitialDataOfSingleMedicalOrder();
  const { payerName } = usePayerNameContextFromSingleOrderLayout();

  const handleNarRpaTriggerClick = () => {
    dispatch(
      setOpenedModalId({
        id: ModalId.NAR_RPA_TRIGGER_MODAL,
        metaData: { orderId: generalOrderDetails?.order_id ?? "" },
      }),
    );
    logTriggerRpaClickedNar({
      generalOrderDetails,
      user,
      payerName,
    });
  };

  const handleCommentRpaModalSave = () => {
    fireMarkAsCompletedAnalytics({
      modalName: ModalNamesForBigQuery.COMMENT_RPA_MODAL_NEW,
      generalOrderDetails,
      authOnFile,
      coverageDetails,
      user,
      oldAuthStatus: oldAuthStatusFromContext,
      logJCodeStatusEvents,
    });
    navigateToWorklistsAfterMarkComplete(dispatch, navigate);
  };

  const handleCloseModal = () => {
    dispatch(closeModal());
  };

  return {
    handleNarRpaTriggerClick,
    handleCommentRpaModalSave,
    handleCloseModal,
  };
};
